import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';
const BAUD_RATES = [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200];
const SENSOR_TYPES = ['TEMPERATURE', 'HUMIDITY', 'TEMP_HUMIDITY'];
const inputCls = "w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/60 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-700 transition";
const selectCls = inputCls;
const cardCls = "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700";
const EMPTY_DL = { name: '', company_id: '', region_id: '', port: '', baud_rate: 9600, serial_key: '', serial_number: '', latitude: '', longitude: '', address: '', description: '' };
const PARITY_OPTIONS = [{ value: 'N', label: 'None (N)' }, { value: 'E', label: 'Even (E)' }, { value: 'O', label: 'Odd (O)' }];
const EMPTY_SENSOR = { name: '', sensor_type: 'TEMP_HUMIDITY', data_logger_id: '', slave_id: 1, port: '', baudrate: 4800, parity: 'N', stopbits: 1, bytesize: 8, reg_temp: 0, reg_humidity: 1, scale: 10, interval_sec: 10, description: '', _company_id: '' };

function StatusBadge({ status }) {
  const on = status === 'online';
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${on ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
      <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${on ? 'bg-green-500' : 'bg-red-500'}`} />
      {on ? 'Online' : 'Offline'}
    </span>
  );
}

const DotsIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </svg>
);

export default function Devices() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('loggers');
  const [companies, setCompanies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [dataLoggers, setDataLoggers] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterDL, setFilterDL] = useState('');
  const [showDLForm, setShowDLForm] = useState(false);
  const [showSensorForm, setShowSensorForm] = useState(false);
  const [editDL, setEditDL] = useState(null);
  const [editSensor, setEditSensor] = useState(null);
  const [dlForm, setDLForm] = useState(EMPTY_DL);
  const [sensorForm, setSensorForm] = useState(EMPTY_SENSOR);
  const [savingDL, setSavingDL] = useState(false);
  const [savingSensor, setSavingSensor] = useState(false);
  const [expandedDLId, setExpandedDLId] = useState(null);
  const [expandedSensorId, setExpandedSensorId] = useState(null);

  const canEdit = ['SUPERADMIN', 'AVUK', 'KRYEINSPEKTORI', 'KOMPANIA'].includes(user?.role);
  const canEditDL = ['SUPERADMIN', 'AVUK'].includes(user?.role);
  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` }), []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cR, rR, dlR, sR] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/companies`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/regions`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/data-loggers`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/sensors`, { headers: headers() }),
      ]);
      setCompanies(Array.isArray(cR.data) ? cR.data : []);
      setRegions(Array.isArray(rR.data) ? rR.data : []);
      setDataLoggers(Array.isArray(dlR.data) ? dlR.data : []);
      setSensors(Array.isArray(sR.data) ? sR.data : []);
    } catch { toast.error('Gabim gjate ngarkimit'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const companyName = id => companies.find(c => c.id === id)?.name || '–';
  const regionName = id => regions.find(r => r.id === id)?.name || '–';
  const dlName = id => dataLoggers.find(d => d.id === id)?.name || '–';
  const sensorCountForDL = dlId => sensors.filter(s => s.data_logger_id === dlId).length;

  const filteredDLs = dataLoggers.filter(dl => (!filterCompany || dl.company_id === filterCompany) && (!filterRegion || dl.region_id === filterRegion));
  const filteredSensors = sensors.filter(s => (!filterCompany || s.company_id === filterCompany) && (!filterDL || s.data_logger_id === filterDL));
  const availableDLsForSensor = sensorForm._company_id ? dataLoggers.filter(d => d.company_id === sensorForm._company_id) : dataLoggers;

  const openAddDL = () => { setEditDL(null); setDLForm(EMPTY_DL); setShowDLForm(true); setShowSensorForm(false); setTimeout(() => document.getElementById('dl-name-input')?.focus(), 50); };
  const openEditDL = dl => {
    setEditDL(dl);
    setDLForm({ name: dl.name || '', company_id: dl.company_id || '', region_id: dl.region_id || '', port: dl.port || '', baud_rate: dl.baud_rate || 9600, serial_key: dl.serial_key || '', serial_number: dl.serial_number || '', latitude: dl.latitude ?? '', longitude: dl.longitude ?? '', address: dl.address || '', description: dl.description || '' });
    setShowDLForm(true); setShowSensorForm(false); setTimeout(() => document.getElementById('dl-name-input')?.focus(), 50);
  };
  const cancelDL = () => { setShowDLForm(false); setEditDL(null); setDLForm(EMPTY_DL); };

  const saveDL = async e => {
    e.preventDefault();
    if (!dlForm.name || !dlForm.company_id) { toast.error('Emri dhe Kompania jane te detyrueshme'); return; }
    setSavingDL(true);
    const payload = { ...dlForm, baud_rate: parseInt(dlForm.baud_rate), serial_key: dlForm.serial_key || null, latitude: dlForm.latitude !== '' ? parseFloat(dlForm.latitude) : null, longitude: dlForm.longitude !== '' ? parseFloat(dlForm.longitude) : null };
    try {
      if (editDL) { await axios.put(`${BACKEND_URL}/api/data-loggers/${editDL.id}`, payload, { headers: headers() }); toast.success('Data Logger u perditesua'); }
      else { await axios.post(`${BACKEND_URL}/api/data-loggers`, payload, { headers: headers() }); toast.success('Data Logger u shtua'); }
      cancelDL(); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gabim'); }
    finally { setSavingDL(false); }
  };

  const deleteDL = async id => {
    if (!window.confirm('A jeni i sigurt?')) return;
    try { await axios.delete(`${BACKEND_URL}/api/data-loggers/${id}`, { headers: headers() }); toast.success('Data Logger u fshi'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gabim'); }
  };

  const openAddSensor = () => { setEditSensor(null); setSensorForm(EMPTY_SENSOR); setShowSensorForm(true); setShowDLForm(false); setTimeout(() => document.getElementById('sensor-name-input')?.focus(), 50); };
  const openEditSensor = s => {
    setEditSensor(s);
    const dl = dataLoggers.find(d => d.id === s.data_logger_id);
    setSensorForm({ name: s.name || '', sensor_type: s.sensor_type || 'TEMP_HUMIDITY', data_logger_id: s.data_logger_id || '', slave_id: s.slave_id ?? 1, port: s.port || dl?.port || '', baudrate: s.baudrate ?? 4800, parity: s.parity || 'N', stopbits: s.stopbits ?? 1, bytesize: s.bytesize ?? 8, reg_temp: s.reg_temp ?? 0, reg_humidity: s.reg_humidity ?? 1, scale: s.scale ?? 10, interval_sec: s.interval_sec ?? 10, description: s.description || '', _company_id: dl?.company_id || '' });
    setShowSensorForm(true); setShowDLForm(false); setTimeout(() => document.getElementById('sensor-name-input')?.focus(), 50);
  };
  const cancelSensor = () => { setShowSensorForm(false); setEditSensor(null); setSensorForm(EMPTY_SENSOR); };

  const saveSensor = async e => {
    e.preventDefault();
    if (!sensorForm.name || !sensorForm.data_logger_id) { toast.error('Emri dhe Data Logger jane te detyrueshme'); return; }
    setSavingSensor(true);
    const { _company_id, ...payload } = sensorForm;
    payload.slave_id = parseInt(payload.slave_id);
    payload.baudrate = parseInt(payload.baudrate);
    payload.stopbits = parseInt(payload.stopbits);
    payload.bytesize = parseInt(payload.bytesize);
    payload.reg_temp = parseInt(payload.reg_temp);
    payload.reg_humidity = parseInt(payload.reg_humidity);
    payload.scale = parseFloat(payload.scale);
    payload.interval_sec = parseInt(payload.interval_sec);
    try {
      if (editSensor) { await axios.put(`${BACKEND_URL}/api/sensors/${editSensor.id}`, payload, { headers: headers() }); toast.success('Sensori u perditesua'); }
      else { await axios.post(`${BACKEND_URL}/api/sensors`, payload, { headers: headers() }); toast.success('Sensori u shtua'); }
      cancelSensor(); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gabim'); }
    finally { setSavingSensor(false); }
  };

  const deleteSensor = async id => {
    if (!window.confirm('A jeni i sigurt?')) return;
    try { await axios.delete(`${BACKEND_URL}/api/sensors/${id}`, { headers: headers() }); toast.success('Sensori u fshi'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gabim'); }
  };

  const tabSwitch = id => { setActiveTab(id); cancelDL(); cancelSensor(); };

  return (
    <Layout>
      <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-full space-y-4">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('devices')}</h1>

        <div className="flex border-b border-gray-200 dark:border-gray-700 gap-1">
          {[{ id: 'loggers', label: t('dataLoggers') }, { id: 'sensors', label: t('sensors') }].map(tab => (
            <button key={tab.id} onClick={() => tabSwitch(tab.id)}
              className={`px-4 py-2 text-sm font-medium transition border-b-2 -mb-px ${activeTab === tab.id ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'loggers' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className={`${selectCls} w-auto`}>
                <option value="">{t('companies')} – te gjitha</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)} className={`${selectCls} w-auto`}>
                <option value="">{t('regions')} – te gjitha</option>
                {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              {canEditDL && !showDLForm && (
                <button onClick={openAddDL} className="ml-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg flex items-center gap-2 transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  {t('addDataLogger')}
                </button>
              )}
            </div>

            {showDLForm && (
              <div className={`${cardCls} overflow-hidden`}>
                {/* Header me gradient */}
                <div className="px-4 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-900/20 dark:to-transparent">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{editDL ? t('editDataLogger') : t('addDataLogger')}</h2>
                  </div>
                  <button type="button" onClick={cancelDL} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-lg leading-none">&times;</button>
                </div>

                <form onSubmit={saveDL} className="p-4 space-y-4">
                  {/* Grup 1: Identifikimi */}
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Identifikimi</p>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Emri *</label>
                      <input id="dl-name-input" className={inputCls} value={dlForm.name} onChange={e => setDLForm(p => ({ ...p, name: e.target.value }))} placeholder="p.sh. DL-Gjakova-01" required />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Nr. Serial</label>
                      <input className={inputCls} value={dlForm.serial_number} onChange={e => setDLForm(p => ({ ...p, serial_number: e.target.value }))} placeholder="DIO-XXXXXX" />
                    </div>
                  </div>

                  {/* Grup 2: Lidhja */}
                  <div className="space-y-3 pt-1">
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Lidhja RS485</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Port (RS485)</label>
                        <input className={inputCls} value={dlForm.port} onChange={e => setDLForm(p => ({ ...p, port: e.target.value }))} placeholder="/dev/ttyS2 ose COM1" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Baud Rate</label>
                        <select className={selectCls} value={dlForm.baud_rate} onChange={e => setDLForm(p => ({ ...p, baud_rate: e.target.value }))}>
                          {BAUD_RATES.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Serial Key (MQTT Provisioning)</label>
                      <input className={inputCls} value={dlForm.serial_key} onChange={e => setDLForm(p => ({ ...p, serial_key: e.target.value }))} placeholder="p.sh. pi-frigofood-mag1" />
                      <p className="text-xs text-gray-400 mt-1">Vlera e <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">SERIAL_KEY</code> nga skedari <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">.env</code> i Pi-t</p>
                    </div>
                  </div>

                  {/* Grup 3: Vendosja */}
                  <div className="space-y-3 pt-1">
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Vendosja</p>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Kompania *</label>
                      <select className={selectCls} value={dlForm.company_id} onChange={e => setDLForm(p => ({ ...p, company_id: e.target.value }))} required>
                        <option value="">– Zgjidh kompaninë –</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Rajoni</label>
                      <select className={selectCls} value={dlForm.region_id} onChange={e => setDLForm(p => ({ ...p, region_id: e.target.value }))}>
                        <option value="">– Zgjidh rajonin –</option>
                        {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Adresa</label>
                      <input className={inputCls} value={dlForm.address} onChange={e => setDLForm(p => ({ ...p, address: e.target.value }))} placeholder="Rr. Liria nr.1" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Lat</label>
                        <input type="number" step="any" className={inputCls} value={dlForm.latitude} onChange={e => setDLForm(p => ({ ...p, latitude: e.target.value }))} placeholder="42.6629" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Lng</label>
                        <input type="number" step="any" className={inputCls} value={dlForm.longitude} onChange={e => setDLForm(p => ({ ...p, longitude: e.target.value }))} placeholder="21.1655" />
                      </div>
                    </div>
                  </div>

                  {/* Pershkrim */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Pershkrimi</label>
                    <textarea rows={2} className={inputCls} value={dlForm.description} onChange={e => setDLForm(p => ({ ...p, description: e.target.value }))} placeholder="Shënim opsional..." />
                  </div>

                  {/* Butonat */}
                  <div className="flex gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <button type="submit" disabled={savingDL} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition">
                      {savingDL ? 'Duke ruajtur...' : (editDL ? '✓ Perditeso' : '+ Shto')}
                    </button>
                    <button type="button" onClick={cancelDL} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl transition">Anulo</button>
                  </div>
                </form>
              </div>
            )}

            {/* MOBILE: Data Logger card list */}
            <div className="md:hidden">
              {loading ? (
                <div className="p-8 text-center text-gray-400">{t('loading')}</div>
              ) : filteredDLs.length === 0 ? (
                <div className="p-8 text-center text-gray-400">{t('noDataLoggers')}</div>
              ) : (
                <div className="space-y-2">
                  {filteredDLs.map(dl => (
                    <div key={dl.id} className={`${cardCls} overflow-hidden`}>
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-white truncate">{dl.name}</p>
                          {dl.serial_number && (
                            <p className="text-xs text-gray-400 mt-0.5">{dl.serial_number}</p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{companyName(dl.company_id)}</p>
                          <div className="mt-1.5">
                            <StatusBadge status={dl.status} />
                          </div>
                        </div>
                        <button
                          onClick={() => setExpandedDLId(expandedDLId === dl.id ? null : dl.id)}
                          className="ml-3 p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition flex-shrink-0"
                          aria-label="Shfaq me shume"
                        >
                          <DotsIcon />
                        </button>
                      </div>
                      {expandedDLId === dl.id && (
                        <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700/30 space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">Port RS485:</span>
                            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-700 dark:text-gray-300">{dl.port || '–'}</code>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">Baud Rate:</span>
                            <span className="text-gray-700 dark:text-gray-300">{dl.baud_rate || '–'}</span>
                          </div>
                          {dl.serial_key && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">Serial Key:</span>
                              <code className="text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">{dl.serial_key}</code>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">Regjioni:</span>
                            <span className="text-gray-700 dark:text-gray-300">{regionName(dl.region_id)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">Sensore:</span>
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold">{sensorCountForDL(dl.id)}</span>
                          </div>
                          {canEditDL && (
                            <div className="flex gap-2 pt-1">
                              <button
                                onClick={() => { openEditDL(dl); setExpandedDLId(null); }}
                                className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition text-center"
                              >
                                Ndrysho
                              </button>
                              <button
                                onClick={() => deleteDL(dl.id)}
                                className="flex-1 px-3 py-1.5 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 text-xs font-medium rounded-lg transition text-center"
                              >
                                Fshi
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* DESKTOP: existing table */}
            <div className={`${cardCls} overflow-hidden hidden md:block`}>
              {loading ? <div className="p-8 text-center text-gray-400">{t('loading')}</div>
                : filteredDLs.length === 0 ? <div className="p-8 text-center text-gray-400">{t('noDataLoggers')}</div>
                  : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                            {['Emri', 'Kompania', 'Regjioni', 'Port RS485', 'Baud', 'Serial Key', 'Sensore', 'Statusi', 'Veprime'].map(h => (
                              <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {filteredDLs.map(dl => (
                            <tr key={dl.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition ${editDL?.id === dl.id ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}>
                              <td className="px-4 py-3"><p className="font-medium text-gray-900 dark:text-white">{dl.name}</p>{dl.serial_number && <p className="text-xs text-gray-400">{dl.serial_number}</p>}</td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{companyName(dl.company_id)}</td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{regionName(dl.region_id)}</td>
                              <td className="px-4 py-3"><code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-700 dark:text-gray-300">{dl.port || '–'}</code></td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{dl.baud_rate || '–'}</td>
                              <td className="px-4 py-3">{dl.serial_key ? <code className="text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">{dl.serial_key}</code> : <span className="text-gray-400">–</span>}</td>
                              <td className="px-4 py-3"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold">{sensorCountForDL(dl.id)}</span></td>
                              <td className="px-4 py-3"><StatusBadge status={dl.status} /></td>
                              <td className="px-4 py-3">{canEditDL && <div className="flex items-center gap-2">
                                <button onClick={() => openEditDL(dl)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                                <button onClick={() => deleteDL(dl.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                              </div>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
            </div>
          </div>
        )}

        {activeTab === 'sensors' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className={`${selectCls} w-auto`}>
                <option value="">{t('companies')} – te gjitha</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={filterDL} onChange={e => setFilterDL(e.target.value)} className={`${selectCls} w-auto`}>
                <option value="">{t('dataLoggers')} – te gjitha</option>
                {(filterCompany ? dataLoggers.filter(d => d.company_id === filterCompany) : dataLoggers).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {canEdit && !showSensorForm && (
                <button onClick={openAddSensor} className="ml-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg flex items-center gap-2 transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  {t('addSensor')}
                </button>
              )}
            </div>

            {showSensorForm && (
              <div className={`${cardCls} overflow-hidden`}>
                {/* Header */}
                <div className="px-4 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-green-50 to-transparent dark:from-green-900/20 dark:to-transparent">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{editSensor ? t('editSensor') : t('addSensor')}</h2>
                  </div>
                  <button type="button" onClick={cancelSensor} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-lg leading-none">&times;</button>
                </div>

                <form onSubmit={saveSensor} className="p-4 space-y-4">
                  {/* Identifikimi */}
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Identifikimi</p>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Emri *</label>
                      <input id="sensor-name-input" className={inputCls} value={sensorForm.name} onChange={e => setSensorForm(p => ({ ...p, name: e.target.value }))} placeholder="p.sh. Sensor Magazina A" required />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Tipi</label>
                        <select className={selectCls} value={sensorForm.sensor_type} onChange={e => setSensorForm(p => ({ ...p, sensor_type: e.target.value }))}>
                          {SENSOR_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Intervali (sekonda)</label>
                        <input type="number" min={1} max={3600} className={inputCls} value={sensorForm.interval_sec} onChange={e => setSensorForm(p => ({ ...p, interval_sec: e.target.value }))} />
                      </div>
                    </div>
                  </div>

                  {/* Lidhja */}
                  <div className="space-y-3 pt-1">
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Lidhja</p>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Kompania</label>
                      <select className={selectCls} value={sensorForm._company_id} onChange={e => setSensorForm(p => ({ ...p, _company_id: e.target.value, data_logger_id: '', port: '' }))}>
                        <option value="">– Zgjidh kompaninë –</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Data Logger *</label>
                      <select className={selectCls} value={sensorForm.data_logger_id} onChange={e => {
                        const dl = dataLoggers.find(d => d.id === e.target.value);
                        setSensorForm(p => ({ ...p, data_logger_id: e.target.value, port: dl?.port || p.port, baudrate: dl?.baud_rate || p.baudrate }));
                      }} required>
                        <option value="">– Zgjidh data loggerin –</option>
                        {availableDLsForSensor.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Modbus RTU */}
                  <div className="space-y-3 pt-1">
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Modbus RTU</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Port RS485</label>
                        <input className={inputCls} value={sensorForm.port} onChange={e => setSensorForm(p => ({ ...p, port: e.target.value }))} placeholder="/dev/ttyUSB0" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Slave ID (1-247)</label>
                        <input type="number" min={1} max={247} className={inputCls} value={sensorForm.slave_id} onChange={e => setSensorForm(p => ({ ...p, slave_id: e.target.value }))} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Baud Rate</label>
                        <select className={selectCls} value={sensorForm.baudrate} onChange={e => setSensorForm(p => ({ ...p, baudrate: e.target.value }))}>
                          {BAUD_RATES.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Parity</label>
                        <select className={selectCls} value={sensorForm.parity} onChange={e => setSensorForm(p => ({ ...p, parity: e.target.value }))}>
                          {PARITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Stop Bits</label>
                        <select className={selectCls} value={sensorForm.stopbits} onChange={e => setSensorForm(p => ({ ...p, stopbits: e.target.value }))}>
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Byte Size</label>
                        <select className={selectCls} value={sensorForm.bytesize} onChange={e => setSensorForm(p => ({ ...p, bytesize: e.target.value }))}>
                          <option value={7}>7</option>
                          <option value={8}>8</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Reg. Temp</label>
                        <input type="number" min={0} className={inputCls} value={sensorForm.reg_temp} onChange={e => setSensorForm(p => ({ ...p, reg_temp: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Reg. Hum</label>
                        <input type="number" min={0} className={inputCls} value={sensorForm.reg_humidity} onChange={e => setSensorForm(p => ({ ...p, reg_humidity: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Scale</label>
                        <input type="number" step="any" className={inputCls} value={sensorForm.scale} onChange={e => setSensorForm(p => ({ ...p, scale: e.target.value }))} />
                      </div>
                    </div>
                  </div>

                  {/* Pershkrim */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Pershkrimi</label>
                    <input className={inputCls} value={sensorForm.description} onChange={e => setSensorForm(p => ({ ...p, description: e.target.value }))} placeholder="Shënim opsional..." />
                  </div>

                  {/* Butonat */}
                  <div className="flex gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <button type="submit" disabled={savingSensor} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition">
                      {savingSensor ? 'Duke ruajtur...' : (editSensor ? '✓ Perditeso' : '+ Shto')}
                    </button>
                    <button type="button" onClick={cancelSensor} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl transition">{t('cancel')}</button>
                  </div>
                </form>
              </div>
            )}

            {/* MOBILE: Sensor card list */}
            <div className="md:hidden">
              {loading ? (
                <div className="p-8 text-center text-gray-400">{t('loading')}</div>
              ) : filteredSensors.length === 0 ? (
                <div className="p-8 text-center text-gray-400">{t('noSensors')}</div>
              ) : (
                <div className="space-y-2">
                  {filteredSensors.map(s => (
                    <div key={s.id} className={`${cardCls} overflow-hidden`}>
                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer"
                        onClick={() => navigate(`/sensors/${s.id}`)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-white truncate">{s.name}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-medium">{s.sensor_type}</span>
                            {s.temperature != null && (
                              <span className="font-semibold text-orange-500 text-xs">{s.temperature.toFixed(1)}C</span>
                            )}
                            {s.humidity != null && (
                              <span className="font-semibold text-cyan-500 text-xs">{s.humidity.toFixed(1)}%</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={e => { e.stopPropagation(); setExpandedSensorId(expandedSensorId === s.id ? null : s.id); }}
                          className="ml-3 p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition flex-shrink-0"
                          aria-label="Shfaq me shume"
                        >
                          <DotsIcon />
                        </button>
                      </div>
                      {expandedSensorId === s.id && (
                        <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700/30 space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400 w-28 flex-shrink-0">Data Logger:</span>
                            <span className="text-gray-700 dark:text-gray-300">{dlName(s.data_logger_id)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400 w-28 flex-shrink-0">Unit ID:</span>
                            <span className="text-gray-700 dark:text-gray-300">{s.unit_id}</span>
                          </div>
                          {s.port && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-500 dark:text-gray-400 w-28 flex-shrink-0">Port:</span>
                              <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-700 dark:text-gray-300">{s.port}</code>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 dark:text-gray-400 w-28 flex-shrink-0">Statusi:</span>
                            <StatusBadge status={s.status} />
                          </div>
                          {s.description && (
                            <div className="flex items-start gap-2 text-sm">
                              <span className="text-gray-500 dark:text-gray-400 w-28 flex-shrink-0">Pershkrimi:</span>
                              <span className="text-gray-700 dark:text-gray-300 text-xs">{s.description}</span>
                            </div>
                          )}
                          {canEdit && (
                            <div className="flex gap-2 pt-1">
                              <button
                                onClick={e => { e.stopPropagation(); openEditSensor(s); setExpandedSensorId(null); }}
                                className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition text-center"
                              >
                                Ndrysho
                              </button>
                              <button
                                onClick={e => { e.stopPropagation(); deleteSensor(s.id); }}
                                className="flex-1 px-3 py-1.5 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 text-xs font-medium rounded-lg transition text-center"
                              >
                                Fshi
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* DESKTOP: existing table */}
            <div className={`${cardCls} overflow-hidden hidden md:block`}>
              {loading ? <div className="p-8 text-center text-gray-400">{t('loading')}</div>
                : filteredSensors.length === 0 ? <div className="p-8 text-center text-gray-400">{t('noSensors')}</div>
                  : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                            {['Emri', 'Data Logger', 'Tipi', 'Unit ID', 'Port', 'Temp (C)', 'Lagestia (%)', 'Statusi', 'Veprime'].map(h => (
                              <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {filteredSensors.map(s => (
                            <tr key={s.id} onClick={() => navigate(`/sensors/${s.id}`)} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition cursor-pointer ${editSensor?.id === s.id ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}>
                              <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{s.name}</td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{dlName(s.data_logger_id)}</td>
                              <td className="px-4 py-3"><span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-medium">{s.sensor_type}</span></td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{s.unit_id}</td>
                              <td className="px-4 py-3">{s.port ? <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-700 dark:text-gray-300">{s.port}</code> : <span className="text-gray-400">–</span>}</td>
                              <td className="px-4 py-3">{s.temperature != null ? <span className="font-semibold text-orange-500">{s.temperature.toFixed(1)}C</span> : <span className="text-gray-400">–</span>}</td>
                              <td className="px-4 py-3">{s.humidity != null ? <span className="font-semibold text-cyan-500">{s.humidity.toFixed(1)}%</span> : <span className="text-gray-400">–</span>}</td>
                              <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                              <td className="px-4 py-3" onClick={e => e.stopPropagation()}>{canEdit && <div className="flex items-center gap-2">
                                <button onClick={() => openEditSensor(s)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                                <button onClick={() => deleteSensor(s.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                              </div>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
